import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics, DateRange } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon, CalendarIcon, FilterIcon, ClockIcon, AwardIcon, AlertTriangleIcon, ChevronDownIcon, FileTextIcon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
// All date helpers return Costa Rica local date (UTC-6).
// Using UTC `.toISOString()` would give the wrong day for users in CR
// late at night (it'd flip to tomorrow's date).
function getCRDate(d: Date = new Date()): Date {
  // Convert to CR time by adjusting the timestamp by the difference
  // between UTC and CR offsets.
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utcMs - 6 * 60 * 60000);
}
function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
function normalizeChannel(ch: string): string {
  const lower = (ch || '').toLowerCase();
  if (lower.includes('qr') || lower.includes('self')) return 'QR';
  if (lower.includes('waiter') || lower.includes('mesero')) return 'Mesero';
  if (lower.includes('web') || lower.includes('online')) return 'Web';
  if (lower.includes('caja') || lower.includes('cashier')) return 'Caja';
  return ch || 'Otro';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Web: '#f59e0b',
  Caja: '#8b5cf6',
  Otro: '#6b7280'
};
function getDaysAgo(days: number): string {
  const d = getCRDate();
  d.setDate(d.getDate() - days);
  return formatYMD(d);
}
function getToday(): string {
  return formatYMD(getCRDate());
}
function getFirstDayOfMonth(): string {
  const d = getCRDate();
  d.setDate(1);
  return formatYMD(d);
}
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm z-50" data-id="element-5429">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-5430">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-5431">
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color || entry.fill
      }} data-id="element-5432" />
          <span className="text-white/50" data-id="element-5433">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-5434">
            {isCurrency ? formatCurrency(Number(entry.value ?? 0)) : Number(entry.value ?? 0).toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-5435">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-5436" />
      <div className="relative" data-id="element-5437">
        <div className="flex items-center justify-between mb-4" data-id="element-5438">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-5439">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-5440">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-5441">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-5442">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const [restaurantId, setRestaurantId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: getDaysAgo(30),
    to: getToday()
  });
  const [datePreset, setDatePreset] = useState<string>('30d');
  const {
    data,
    loading,
    error,
    refetch,
    restaurants
  } = useNoizaMetrics(restaurantId, dateRange);
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const to = getToday();
    let from = to;
    if (preset === 'all') from = '2020-01-01';else if (preset === 'today') from = to;else if (preset === '7d') from = getDaysAgo(7);else if (preset === '30d') from = getDaysAgo(30);else if (preset === 'month') from = getFirstDayOfMonth();
    setDateRange({
      from,
      to
    });
  };
  // Derived Data for Charts
  const dailyChartData = useMemo(() => {
    const source = data.salesDaily.length > 0 ? data.salesDaily : data.gmvDaily;
    // Aggregate by day if multiple restaurants
    const aggregated = source.reduce((acc: any, curr: any) => {
      const day = curr.day;
      if (!acc[day]) acc[day] = {
        day,
        label: formatDayLabel(day),
        gmv: 0,
        orders: 0
      };
      acc[day].gmv += curr.gmv;
      acc[day].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => a.day.localeCompare(b.day));
  }, [data.salesDaily, data.gmvDaily]);
  const hourlyChartData = useMemo(() => {
    const aggregated = data.salesHourly.reduce((acc: any, curr: any) => {
      const hour = curr.hour;
      if (!acc[hour]) acc[hour] = {
        hour,
        label: `${hour}:00`,
        gmv: 0,
        orders: 0
      };
      acc[hour].gmv += curr.gmv;
      acc[hour].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => Number(a.hour) - Number(b.hour));
  }, [data.salesHourly]);
  const channelChartData = useMemo(() => {
    return data.channelDistribution.map(d => ({
      name: normalizeChannel(d.channel),
      value: d.orders,
      pct: d.pct,
      color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
    })).filter(d => d.value > 0);
  }, [data.channelDistribution]);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  const topRestaurantsChartData = useMemo(() => {
    return data.gmvByRestaurant.slice(0, 5).map(r => ({
      name: String(r.restaurant_name || '').length > 15 ? String(r.restaurant_name || '').substring(0, 15) + '...' : String(r.restaurant_name || ''),
      fullName: r.restaurant_name,
      gmv: r.gmv
    }));
  }, [data.gmvByRestaurant]);
  const topProductsChartData = useMemo(() => {
    return data.topProducts.slice(0, 5).map(p => ({
      name: String(p.product_name || '').length > 15 ? String(p.product_name || '').substring(0, 15) + '...' : String(p.product_name || ''),
      fullName: p.product_name,
      revenue: p.revenue,
      quantity: p.quantity
    }));
  }, [data.topProducts]);
  // Insights
  const insights = useMemo(() => {
    let bestDay = {
      day: '-',
      val: 0
    };
    let worstDay = {
      day: '-',
      val: Infinity
    };
    dailyChartData.forEach((d: any) => {
      if (d.gmv > bestDay.val) bestDay = {
        day: d.label,
        val: d.gmv
      };
      if (d.gmv < worstDay.val && d.gmv > 0) worstDay = {
        day: d.label,
        val: d.gmv
      };
    });
    let bestHour = {
      hour: '-',
      val: 0
    };
    let worstHour = {
      hour: '-',
      val: Infinity
    };
    hourlyChartData.forEach((d: any) => {
      if (d.gmv > bestHour.val) bestHour = {
        hour: d.label,
        val: d.gmv
      };
      if (d.gmv < worstHour.val && d.gmv > 0) worstHour = {
        hour: d.label,
        val: d.gmv
      };
    });
    let bestRest = data.gmvByRestaurant[0];
    let worstRest = data.gmvByRestaurant[data.gmvByRestaurant.length - 1];
    return {
      bestDay,
      worstDay,
      bestHour,
      worstHour,
      bestRest,
      worstRest
    };
  }, [dailyChartData, hourlyChartData, data.gmvByRestaurant]);
  return <div className="min-h-screen bg-[#0a0a0f] text-white pb-20" data-id="element-5443">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-5444">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5445">
          <div className="flex items-center gap-4" data-id="element-5446">
            <Link to="/admin/enrollment" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-5447">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-5448" />
            </Link>
            <div data-id="element-5449">
              <div className="flex items-center gap-2.5" data-id="element-5450">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-5451">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-5452" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-5453">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-5454">
                  Executive Dashboard
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-5455">
            <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-5456">
              {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-5457" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-5458" />}
              <span className="hidden sm:inline" data-id="element-5459">Actualizar</span>
            </button>
          </div>
        </div>
      </header>

      {/* ─── Filters Bar ────────────────────────────────── */}
      <div className="bg-[#12121a] border-b border-white/[0.06]" data-id="element-5460">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-5461">
          <div className="flex items-center gap-3" data-id="element-5462">
            <FilterIcon className="w-4 h-4 text-white/40" data-id="element-5463" />
            <div className="relative" data-id="element-5464">
              <select value={restaurantId} onChange={e => setRestaurantId(e.target.value)} className="appearance-none bg-white/[0.06] border border-white/[0.1] rounded-lg pl-3 pr-8 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors" data-id="element-5465">
                <option value="all" data-id="element-5466">Todos los restaurantes</option>
                {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-5467">
                    {r.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="w-4 h-4 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5468" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pb-1 sm:pb-0" data-id="element-5469">
            <CalendarIcon className="w-4 h-4 text-white/40 mr-1" data-id="element-5470" />
            {[{
            id: 'all',
            label: 'Todo'
          }, {
            id: 'today',
            label: 'Hoy'
          }, {
            id: '7d',
            label: '7 días'
          }, {
            id: '30d',
            label: '30 días'
          }, {
            id: 'month',
            label: 'Mes actual'
          }].map(preset => <button key={preset.id} onClick={() => handleDatePreset(preset.id)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${datePreset === preset.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/[0.03] text-white/60 border border-white/[0.05] hover:bg-white/[0.08]'}`} data-id="element-5471">
                {preset.label}
              </button>)}

            <div className="h-5 w-px bg-white/10 mx-1" data-id="element-5472" />

            <div className="flex items-center gap-2" data-id="element-5473">
              <label className="text-xs text-white/40" data-id="element-5474">Desde</label>
              <input type="date" value={dateRange.from} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                from: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-5475" />
              <label className="text-xs text-white/40" data-id="element-5476">Hasta</label>
              <input type="date" value={dateRange.to} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                to: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-5477" />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-5478">
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400 flex items-center gap-3" data-id="element-5479">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-5480" />
            {error}
          </div>}

        {loading ? <div className="space-y-8" data-id="element-5481">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-5482">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5483" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5484">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5485" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5486" />
            </div>
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-5487">
              <KpiCard label="GMV Total" value={formatCompactCurrency(Number(data.gmv.total_gmv ?? 0))} subtitle="Ingresos brutos" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-5489" />} accentColor="#10b981" data-id="element-5488" />
              <KpiCard label="Órdenes" value={Number(data.gmv.total_orders ?? 0).toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-5491" />} accentColor="#3b82f6" data-id="element-5490" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(Number(data.gmv.avg_ticket ?? 0))} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-5493" />} accentColor="#f59e0b" data-id="element-5492" />
              <KpiCard label="Clientes Únicos" value={Number(data.uniqueCustomers ?? 0).toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-5495" />} accentColor="#06b6d4" data-id="element-5494" />
              <KpiCard label="Recurrentes" value={Number(data.repeatCustomers.repeat_count ?? 0).toLocaleString()} subtitle={Number(data.repeatCustomers.repeat_pct ?? 0) > 0 ? `${Math.round(Number(data.repeatCustomers.repeat_pct ?? 0))}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-5497" />} accentColor="#ec4899" data-id="element-5496" />
              <KpiCard label="Autoservicio" value={`${Math.round(Number(data.selfServicePct.self_service_pct ?? 0))}%`} subtitle={`Mesero: ${Math.round(Number(data.selfServicePct.waiter_pct ?? 0))}%`} icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-5499" />} accentColor="#8b5cf6" data-id="element-5498" />
            </div>

            {/* ─── Insights Section ───────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5500">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 p-4 flex items-start gap-4" data-id="element-5501">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-5502">
                  <AwardIcon className="w-5 h-5 text-emerald-400" data-id="element-5503" />
                </div>
                <div data-id="element-5504">
                  <h3 className="text-sm font-semibold text-emerald-400" data-id="element-5505">
                    Mejor Día
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-5506">
                    {insights.bestDay.day}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5507">
                    {formatCurrency(insights.bestDay.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 p-4 flex items-start gap-4" data-id="element-5508">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0" data-id="element-5509">
                  <ClockIcon className="w-5 h-5 text-blue-400" data-id="element-5510" />
                </div>
                <div data-id="element-5511">
                  <h3 className="text-sm font-semibold text-blue-400" data-id="element-5512">
                    Hora Pico
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-5513">
                    {insights.bestHour.hour}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5514">
                    {formatCurrency(insights.bestHour.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-4 flex items-start gap-4" data-id="element-5515">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0" data-id="element-5516">
                  <StoreIcon className="w-5 h-5 text-amber-400" data-id="element-5517" />
                </div>
                <div data-id="element-5518">
                  <h3 className="text-sm font-semibold text-amber-400" data-id="element-5519">
                    Top Restaurante
                  </h3>
                  <p className="text-white font-medium mt-1 truncate max-w-[150px]" data-id="element-5520">
                    {insights.bestRest?.restaurant_name || '-'}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5521">
                    {insights.bestRest ? formatCurrency(insights.bestRest.gmv) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* ─── Charts Row 1 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5522">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5523">
                <div className="flex items-center justify-between mb-6" data-id="element-5524">
                  <div data-id="element-5525">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-5526">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-5527">
                      Ingresos brutos por día
                    </p>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-5528">
                    Sin datos
                  </div> : <div className="h-64" data-id="element-5529">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5530">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5531">
                        <defs data-id="element-5532">
                          <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-5533">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-5534" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-5535" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5536" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-5537" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5538" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5540" />} data-id="element-5539" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#gmvGrad)" data-id="element-5541" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5542">
                <div className="mb-4" data-id="element-5543">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5544">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5545">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-5546">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-5547">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-5548">
                        <PieChart data-id="element-5549">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-5550">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-5551" />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-5553">
                                  <p className="font-medium" data-id="element-5554">{d.name}</p>
                                  <p className="text-white/50" data-id="element-5555">
                                    {Number(d.value ?? 0).toLocaleString()}{' '}
                                    pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-5552" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-5556">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-5557">
                            <div className="flex items-center gap-2" data-id="element-5558">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-5559" />
                              <span className="text-sm text-white/60" data-id="element-5560">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-5561">
                              <span className="text-sm font-semibold text-white/90" data-id="element-5562">
                                {Number(entry.value ?? 0).toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-5563">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Charts Row 2 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5564">
              {/* Hourly Sales */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5565">
                <div className="mb-6" data-id="element-5566">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5567">
                    Ventas por Hora
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5568">
                    Distribución a lo largo del día
                  </p>
                </div>
                {hourlyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5569">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5570">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5571">
                      <BarChart data={hourlyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5572">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5573" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} data-id="element-5574" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5575" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5577" />} data-id="element-5576" />
                        <Bar dataKey="gmv" name="GMV" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-5578" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Orders Daily */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5579">
                <div className="mb-6" data-id="element-5580">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5581">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5582">
                    Volumen de pedidos por día
                  </p>
                </div>
                {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5583">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5584">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5585">
                      <BarChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5586">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5587" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-5588" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} data-id="element-5589" />
                        <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-5591" />} data-id="element-5590" />
                        <Bar dataKey="orders" name="Órdenes" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-5592" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Charts Row 3 (Horizontal Bars) ──────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5593">
              {/* Top Restaurants */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5594">
                <div className="mb-6" data-id="element-5595">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5596">
                    Top Restaurantes
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5597">
                    Por GMV generado
                  </p>
                </div>
                {topRestaurantsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5598">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5599">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5600">
                      <BarChart data={topRestaurantsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-5601">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-5602" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5603" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-5604" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5606" />} data-id="element-5605" />
                        <Bar dataKey="gmv" name="GMV" fill="#f59e0b" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-5607" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Top Products */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5608">
                <div className="mb-6" data-id="element-5609">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5610">
                    Top Productos
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5611">
                    Por ingresos generados
                  </p>
                </div>
                {topProductsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5612">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5613">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5614">
                      <BarChart data={topProductsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-5615">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-5616" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5617" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-5618" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5620" />} data-id="element-5619" />
                        <Bar dataKey="revenue" name="Ingresos" fill="#ec4899" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-5621" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Tables Section ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5622">
              {/* Top Products Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-5623">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-5624">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5625">
                    Detalle de Productos
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-5626">
                  <table className="w-full text-sm" data-id="element-5627">
                    <thead data-id="element-5628">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-5629">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-5630">
                          Producto
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-5631">
                          Cant.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-5632">
                          Ingresos
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5633">
                      {data.topProducts.slice(0, 10).map((p, i) => <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-5634">
                          <td className="px-5 py-3" data-id="element-5635">
                            <p className="font-medium text-white/90" data-id="element-5636">
                              {p.product_name}
                            </p>
                            {restaurantId === 'all' && <p className="text-xs text-white/40 mt-0.5" data-id="element-5637">
                                {p.restaurant_name}
                              </p>}
                          </td>
                          <td className="px-5 py-3 text-right text-white/60" data-id="element-5638">
                            {p.quantity}
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-emerald-400" data-id="element-5639">
                            {formatCurrency(p.revenue)}
                          </td>
                        </tr>)}
                      {data.topProducts.length === 0 && <tr data-id="element-5640">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-5641">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Orders Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-5642">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-5643">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5644">
                    Órdenes Recientes
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-5645">
                  <table className="w-full text-sm" data-id="element-5646">
                    <thead data-id="element-5647">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-5648">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-5649">
                          ID / Cliente
                        </th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-5650">
                          Estado
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-5651">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5652">
                      {data.recentOrders.slice(0, 10).map((o, i) => <tr key={o.id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-5653">
                          <td className="px-5 py-3" data-id="element-5654">
                            <p className="font-medium text-white/90" data-id="element-5655">
                              {String(o.id).substring(0, 8).toUpperCase()}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5" data-id="element-5656">
                              {o.customer_name}
                            </p>
                          </td>
                          <td className="px-5 py-3" data-id="element-5657">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider bg-white/[0.06] text-white/70" data-id="element-5658">
                              {o.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-white" data-id="element-5659">
                            {formatCurrency(o.total)}
                          </td>
                        </tr>)}
                      {data.recentOrders.length === 0 && <tr data-id="element-5660">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-5661">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5662">
              <Link to="/admin/enrollment/receipts" className="bg-[#12121a] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.02] transition-colors group flex items-center justify-between" data-id="element-5663">
                <div className="flex items-center gap-4" data-id="element-5664">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5665">
                    <FileTextIcon className="w-6 h-6 text-emerald-400" data-id="element-5666" />
                  </div>
                  <div data-id="element-5667">
                    <h3 className="text-white font-bold" data-id="element-5668">Recibos de Pago</h3>
                    <p className="text-sm text-white/50 mt-0.5" data-id="element-5669">
                      Generar y consultar recibos
                    </p>
                  </div>
                </div>
                <ArrowLeftIcon className="w-5 h-5 text-white/30 rotate-180 group-hover:text-white/70 transition-colors" data-id="element-5670" />
              </Link>
            </div>
          </>}
      </main>
    </div>;
}